/**
 * Super Admin control plane contracts (admin module, mounted at /api/v1/admin).
 * Every route requires a platform admin (custom claim AND platform_admins row). Lists minimize
 * PII: owner emails are masked; customer PII only through an active support session.
 */
import { z } from 'zod';
import {
  DEFAULT_VAT_RATE_BPS,
  FEATURE_KEYS,
  MAX_VAT_RATE_BPS,
  PLAN_KEYS,
  RESOURCES,
} from '../plans';
import { AI_PURPOSE_CREDIT_CAPS } from '../aiPricing';
import {
  AI_EFFORTS,
  AI_PURPOSES,
  AI_PROVIDERS,
  AiCreditPolicySchema,
  AiPurposeSchema,
  ModelRouteSchema,
} from './ai';
import { IsoDateTimeSchema, UuidSchema } from './common';
import { TENANT_STATUSES } from './tenants';

export const AdminReasonSchema = z.string().trim().min(5).max(500);

// ─── audit actions ──────────────────────────────────────────────────────────
export const PLATFORM_AUDIT_ACTIONS = [
  'credits.gift',
  'credits.correction',
  'credits.extend_expiry',
  'credits.suspend_grant',
  'credits.reinstate_grant',
  'model_route.update',
  'ai_pricing.update',
  'ai_pricing.reset',
  'plan.update',
  'topup_bundle.update',
  'tenant.create_trial',
  'tenant.owner_invitation_resend',
  'tenant.suspend',
  'tenant.unsuspend',
  'tenant.note',
  'support.session_start',
  'support.session_end',
  'support.pii_view',
  'config.update',
] as const;
export type PlatformAuditAction = (typeof PLATFORM_AUDIT_ACTIONS)[number];

// ─── overview ───────────────────────────────────────────────────────────────
export const AdminOverviewQuerySchema = z.object({
  days: z.coerce.number().int().min(7).max(90).default(30),
});

const ChannelTotalsSchema = z.object({
  sent: z.number(),
  delivered: z.number(),
  failed: z.number(),
});

export const AdminOverviewSchema = z.object({
  generatedAt: IsoDateTimeSchema,
  days: z.number(),
  tenants: z.object({
    total: z.number(),
    byStatus: z.record(z.string(), z.number()),
    byPlan: z.record(z.string(), z.number()),
    /** Part of `byStatus.active`: on a free trial, so not paying yet. */
    onTrial: z.number().default(0),
  }),
  mrr: z.object({
    cents: z.number(),
    currency: z.string(),
    activeSubscriptions: z.number(),
    byPlan: z.record(z.string(), z.number()),
  }),
  signups: z.array(z.object({ day: z.string(), count: z.number() })),
  messages: z.object({
    sms: ChannelTotalsSchema,
    email: ChannelTotalsSchema,
    daily: z.array(
      z.object({ day: z.string(), sent: z.number(), delivered: z.number(), failed: z.number() }),
    ),
  }),
  ai: z.object({
    runs: z.number(),
    failedRuns: z.number(),
    costMicros: z.number(),
    creditsCaptured: z.number(),
    daily: z.array(z.object({ day: z.string(), runs: z.number(), costMicros: z.number() })),
  }),
  errors: z.object({
    deliveriesFailed: z.number(),
    agentRunsFailed: z.number(),
    stripeEventsFailed: z.number(),
    providerWebhooksFailed: z.number(),
    workflowRunsFailed: z.number(),
  }),
  webhookBacklog: z.object({ stripe: z.number(), providers: z.number() }),
});
export type AdminOverview = z.infer<typeof AdminOverviewSchema>;

// ─── tenants directory ──────────────────────────────────────────────────────
export const AdminTenantsQuerySchema = z.object({
  q: z.string().trim().max(120).optional(),
  status: z.enum(TENANT_STATUSES).optional(),
  plan: z.enum(PLAN_KEYS).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(25),
  offset: z.coerce.number().int().min(0).max(100_000).default(0),
  sort: z.enum(['created', 'name', 'contacts']).default('created'),
});
export type AdminTenantsQuery = z.input<typeof AdminTenantsQuerySchema>;

const UsageByResourceSchema = z.object({ sms: z.number(), email: z.number(), ai: z.number() });

export const AdminTenantRowSchema = z.object({
  id: UuidSchema,
  name: z.string(),
  slug: z.string(),
  status: z.enum(TENANT_STATUSES),
  planKey: z.enum(PLAN_KEYS).nullable(),
  businessCategory: z.string().nullable(),
  /** Masked (m•••@example.gr). */
  ownerEmailMasked: z.string().nullable(),
  onboarding: z.object({ step: z.string().nullable(), completed: z.number() }),
  contactsCount: z.number(),
  usage30d: UsageByResourceSchema,
  subscriptionStatus: z.string().nullable(),
  /** End of the free trial while it runs or just ended unpaid; null once on a paid subscription. */
  trialEndsAt: IsoDateTimeSchema.nullable().default(null),
  createdAt: IsoDateTimeSchema,
});
export type AdminTenantRow = z.infer<typeof AdminTenantRowSchema>;

export const AdminTenantsResponseSchema = z.object({
  items: z.array(AdminTenantRowSchema),
  total: z.number(),
  limit: z.number(),
  offset: z.number(),
});
export type AdminTenantsResponse = z.infer<typeof AdminTenantsResponseSchema>;

// ─── tenant detail ──────────────────────────────────────────────────────────
export const AdminWalletSchema = z.object({
  resource: z.enum(RESOURCES),
  available: z.number(),
  reserved: z.number(),
  consumed: z.number(),
  expired: z.number(),
  reversed: z.number(),
  totalGranted: z.number(),
});
export type AdminWallet = z.infer<typeof AdminWalletSchema>;

export const AdminGrantSchema = z.object({
  id: UuidSchema,
  resource: z.enum(RESOURCES),
  source: z.string(),
  originalQty: z.number(),
  remainingQty: z.number(),
  reversedQty: z.number(),
  status: z.enum(['active', 'suspended', 'revoked']),
  expiresAt: IsoDateTimeSchema.nullable(),
  reference: z.string(),
  reason: z.string().nullable(),
  createdBy: z.string().nullable(),
  suspendedQty: z.number().nullable(),
  createdAt: IsoDateTimeSchema,
});
export type AdminGrant = z.infer<typeof AdminGrantSchema>;

export const AdminLedgerEntrySchema = z.object({
  id: UuidSchema,
  resource: z.enum(RESOURCES),
  entryType: z.string(),
  quantity: z.number(),
  grantId: UuidSchema.nullable(),
  operationId: z.string().nullable(),
  availableAfter: z.number(),
  actor: z.string().nullable(),
  reason: z.string().nullable(),
  createdAt: IsoDateTimeSchema,
});
export type AdminLedgerEntry = z.infer<typeof AdminLedgerEntrySchema>;

export const AdminNoteSchema = z.object({
  id: UuidSchema,
  authorId: z.string(),
  authorEmail: z.string().nullable(),
  body: z.string(),
  createdAt: IsoDateTimeSchema,
});
export type AdminNote = z.infer<typeof AdminNoteSchema>;

export const SupportSessionSchema = z.object({
  id: UuidSchema,
  tenantId: UuidSchema,
  adminId: z.string(),
  purpose: z.string(),
  scope: z.literal('contacts_pii'),
  expiresAt: IsoDateTimeSchema,
  endedAt: IsoDateTimeSchema.nullable(),
  createdAt: IsoDateTimeSchema,
  active: z.boolean(),
});
export type SupportSession = z.infer<typeof SupportSessionSchema>;

export const AdminTenantDetailSchema = z.object({
  tenant: z.object({
    id: UuidSchema,
    name: z.string(),
    slug: z.string(),
    status: z.enum(TENANT_STATUSES),
    planKey: z.enum(PLAN_KEYS).nullable(),
    businessCategory: z.string().nullable(),
    locale: z.string(),
    timezone: z.string(),
    country: z.string(),
    suspendedAt: IsoDateTimeSchema.nullable(),
    suspendedReason: z.string().nullable(),
    createdAt: IsoDateTimeSchema,
    onboarding: z.object({ step: z.string().nullable(), completed: z.number() }),
  }),
  owner: z.object({ emailMasked: z.string().nullable(), members: z.number() }),
  /** End of the free trial while it runs or just ended unpaid; null once on a paid subscription. */
  trialEndsAt: IsoDateTimeSchema.nullable().default(null),
  /** Open invitation for the first owner of a Super Admin created account. */
  ownerInvitation: z
    .object({
      id: UuidSchema,
      emailMasked: z.string(),
      status: z.enum(['pending', 'expired']),
      expiresAt: IsoDateTimeSchema,
    })
    .nullable()
    .default(null),
  subscription: z
    .object({
      status: z.string(),
      planKey: z.string().nullable(),
      priceCents: z.number().nullable(),
      currency: z.string().nullable(),
      currentPeriodEnd: IsoDateTimeSchema.nullable(),
      cancelAtPeriodEnd: z.boolean(),
      pastDueSince: IsoDateTimeSchema.nullable(),
      hasStripeCustomer: z.boolean(),
    })
    .nullable(),
  wallets: z.array(AdminWalletSchema),
  grants: z.array(AdminGrantSchema),
  ledger: z.array(AdminLedgerEntrySchema),
  counters: z.object({
    contacts: z.number(),
    campaigns: z.record(z.string(), z.number()),
    reviewRequests30d: z.number(),
    googleClicks30d: z.number(),
    agentRuns30d: z.number(),
  }),
  usage: z.array(
    z.object({
      resource: z.enum(RESOURCES),
      purpose: z.string(),
      provider: z.string().nullable(),
      quantity: z.number(),
      costMicros: z.number(),
      events: z.number(),
    }),
  ),
  /** AI runs in the last 30 days by purpose × provider × model (spec §22 #13). */
  aiUsage: z.array(
    z.object({
      purpose: z.string(),
      provider: z.string().nullable(),
      model: z.string().nullable(),
      runs: z.number(),
      failures: z.number(),
      inputTokens: z.number(),
      outputTokens: z.number(),
      cachedTokens: z.number(),
      costMicros: z.number(),
      credits: z.number(),
    }),
  ),
  integrations: z.object({
    /** null while Google Business Profile is switched off platform-wide (GOOGLE_BUSINESS_ENABLED). */
    google: z
      .object({
        status: z.string().nullable(),
        lastSyncAt: IsoDateTimeSchema.nullable(),
        lastError: z.string().nullable(),
        locations: z.number(),
      })
      .nullable(),
    locations: z.number(),
    reviewLinkConfigured: z.boolean(),
  }),
  failures: z.array(
    z.object({
      kind: z.enum(['delivery', 'agent_run', 'workflow_run', 'import']),
      id: z.string(),
      at: IsoDateTimeSchema,
      code: z.string().nullable(),
      detail: z.string().nullable(),
    }),
  ),
  notes: z.array(AdminNoteSchema),
  supportSession: SupportSessionSchema.nullable(),
  reconciliation: z.object({ ok: z.boolean(), mismatches: z.number() }),
});
export type AdminTenantDetail = z.infer<typeof AdminTenantDetailSchema>;

export const AdminNoteRequestSchema = z.object({ body: z.string().trim().min(2).max(4000) });
export const AdminSuspendRequestSchema = z.object({ reason: AdminReasonSchema });

// ─── trial accounts (ADR-B11) ───────────────────────────────────────────────
export const MIN_TRIAL_DAYS = 1;
export const MAX_TRIAL_DAYS = 90;
export const DEFAULT_TRIAL_DAYS = 10;

export const AdminCreateTrialAccountSchema = z.object({
  name: z.string().trim().min(2).max(120),
  ownerEmail: z.email().max(254),
  planKey: z.enum(PLAN_KEYS),
  trialDays: z.number().int().min(MIN_TRIAL_DAYS).max(MAX_TRIAL_DAYS),
  reason: AdminReasonSchema,
});
export type AdminCreateTrialAccount = z.input<typeof AdminCreateTrialAccountSchema>;

export const AdminOwnerInvitationResponseSchema = z.object({
  tenantId: UuidSchema,
  trialEndsAt: IsoDateTimeSchema.nullable(),
  /** The invitation email was accepted by the email provider. */
  emailSent: z.boolean(),
  /** The link itself, so the admin can pass it on when email fails. Only accepted by that email. */
  acceptUrl: z.string(),
});
export type AdminOwnerInvitationResponse = z.infer<typeof AdminOwnerInvitationResponseSchema>;

export const AdminTenantStatusResponseSchema = z.object({
  id: UuidSchema,
  status: z.enum(TENANT_STATUSES),
});

// ─── credit desk ────────────────────────────────────────────────────────────
/** Credit desk actions (spec §5.6). No destructive balance edits: every change is a ledger entry. */
export const CreditDeskActionSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('gift'),
    resource: z.enum(RESOURCES),
    quantity: z.number().int().positive().max(100_000),
    expiresAt: IsoDateTimeSchema.nullable().optional(),
    reason: AdminReasonSchema,
  }),
  z.object({
    type: z.literal('correction'),
    resource: z.enum(RESOURCES),
    direction: z.enum(['add', 'remove']),
    quantity: z.number().int().positive().max(100_000),
    reason: AdminReasonSchema,
  }),
  z.object({
    type: z.literal('extend_expiry'),
    grantId: UuidSchema,
    expiresAt: IsoDateTimeSchema,
    reason: AdminReasonSchema,
  }),
  z.object({ type: z.literal('suspend_grant'), grantId: UuidSchema, reason: AdminReasonSchema }),
  z.object({ type: z.literal('reinstate_grant'), grantId: UuidSchema, reason: AdminReasonSchema }),
]);
export type CreditDeskAction = z.infer<typeof CreditDeskActionSchema>;

export const CreditDeskPreviewSchema = z.object({
  action: CreditDeskActionSchema,
  resource: z.enum(RESOURCES),
  before: AdminWalletSchema,
  after: AdminWalletSchema,
  /** Signed, short-lived token binding this exact action to this admin and tenant. */
  confirmationToken: z.string(),
  expiresAt: IsoDateTimeSchema,
  warnings: z.array(z.string()),
});
export type CreditDeskPreview = z.infer<typeof CreditDeskPreviewSchema>;

export const CreditDeskCommitRequestSchema = z.object({
  action: CreditDeskActionSchema,
  confirmationToken: z.string().min(10).max(500),
});
export type CreditDeskCommitRequest = z.infer<typeof CreditDeskCommitRequestSchema>;

export const CreditDeskResultSchema = z.object({
  auditId: UuidSchema,
  action: CreditDeskActionSchema,
  grantId: UuidSchema.nullable(),
  quantity: z.number(),
  before: AdminWalletSchema,
  after: AdminWalletSchema,
  replayed: z.boolean(),
});
export type CreditDeskResult = z.infer<typeof CreditDeskResultSchema>;

// ─── support sessions ───────────────────────────────────────────────────────
export const SupportSessionRequestSchema = z.object({
  purpose: z.string().trim().min(10).max(500),
  durationMinutes: z.number().int().min(5).max(120).default(30),
});
export const SupportContactsQuerySchema = z.object({
  q: z.string().trim().max(120).optional(),
  limit: z.coerce.number().int().min(1).max(50).default(20),
});
export const SupportContactSchema = z.object({
  id: UuidSchema,
  displayName: z.string().nullable(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  status: z.string(),
  consentEmail: z.string().nullable(),
  consentSms: z.string().nullable(),
  createdAt: IsoDateTimeSchema,
});
export const SupportContactsResponseSchema = z.object({
  session: SupportSessionSchema,
  items: z.array(SupportContactSchema),
});

// ─── plans & bundles ────────────────────────────────────────────────────────
const AllowancesSchema = z.object({
  sms: z.number().int().min(0).max(1_000_000),
  email: z.number().int().min(0).max(1_000_000),
  ai: z.number().int().min(0).max(1_000_000),
});
const LimitsSchema = z.object({
  stores: z.number().int().min(1).max(1000),
  seats: z.number().int().min(1).max(1000),
  contacts: z.number().int().min(0).max(10_000_000).nullable(),
  customFields: z.number().int().min(0).max(10_000),
  automations: z.number().int().min(0).max(10_000),
});
const StripePriceIdSchema = z
  .string()
  .trim()
  .regex(/^price_[A-Za-z0-9_]{3,120}$/)
  .nullable();

export const AdminPlanSchema = z.object({
  key: z.enum(PLAN_KEYS),
  name: z.string(),
  description: z.string().nullable(),
  priceCents: z.number(),
  currency: z.string(),
  interval: z.string(),
  stripePriceId: z.string().nullable(),
  allowances: AllowancesSchema,
  features: z.array(z.enum(FEATURE_KEYS)),
  limits: LimitsSchema,
  active: z.boolean(),
  sortOrder: z.number(),
  version: z.number(),
  updatedAt: IsoDateTimeSchema,
  subscribers: z.number(),
});
export type AdminPlan = z.infer<typeof AdminPlanSchema>;

export const AdminBundleSchema = z.object({
  key: z.string(),
  name: z.string(),
  resource: z.enum(RESOURCES),
  quantity: z.number(),
  priceCents: z.number(),
  currency: z.string(),
  availableOnPlans: z.array(z.enum(PLAN_KEYS)),
  stripePriceId: z.string().nullable(),
  active: z.boolean(),
  sortOrder: z.number(),
  version: z.number(),
  updatedAt: IsoDateTimeSchema,
});
export type AdminBundle = z.infer<typeof AdminBundleSchema>;

/**
 * Platform VAT (ADR-B10): catalog prices are net, checkout adds `rateBps`. `stripeTaxRate` is the
 * Stripe Tax Rate that `scripts/stripe-sync.ts` created for that rate (null in fake mode or before
 * the first sync); real checkout refuses to sell while it does not match `rateBps`.
 */
export const AdminVatSchema = z.object({
  rateBps: z.number().int(),
  updatedAt: IsoDateTimeSchema.nullable(),
  updatedBy: z.string().nullable(),
  stripeTaxRate: z.object({ id: z.string(), rateBps: z.number().int() }).nullable(),
  /**
   * What checkout will use (ADR-B10): `source` env = STRIPE_TAX_RATE_ID, sync = stored by
   * stripe-sync; `ok` true when it is active, exclusive and equal to `rateBps` (checked with
   * Stripe), false with `reason` (no_tax_rate / tax_rate_mismatch / stripe_unavailable), null in
   * fake mode or at 0%.
   */
  stripeStatus: z
    .object({
      id: z.string().nullable(),
      source: z.enum(['env', 'sync']).nullable(),
      ok: z.boolean().nullable(),
      reason: z.string().nullable(),
    })
    .optional(),
  /** Live subscriptions whose VAT rate is not `rateBps` (fix: apply VAT to subscriptions). */
  subscriptionsOutdated: z.number().int().optional(),
});
export type AdminVat = z.infer<typeof AdminVatSchema>;

/** POST /admin/vat/apply-to-subscriptions { reason }. */
export const AdminApplyVatSchema = z.object({ reason: AdminReasonSchema });
export const AdminApplyVatResultSchema = z.object({
  rateBps: z.number().int(),
  total: z.number().int(),
  updated: z.number().int(),
  skipped: z.number().int(),
  failed: z.array(z.object({ tenantId: z.string(), message: z.string() })),
});
export type AdminApplyVatResult = z.infer<typeof AdminApplyVatResultSchema>;

export const AdminCatalogSchema = z.object({
  plans: z.array(AdminPlanSchema),
  bundles: z.array(AdminBundleSchema),
  vat: AdminVatSchema.default({
    rateBps: DEFAULT_VAT_RATE_BPS,
    updatedAt: null,
    updatedBy: null,
    stripeTaxRate: null,
  }),
});
export type AdminCatalog = z.infer<typeof AdminCatalogSchema>;

export const AdminUpdatePlanSchema = z.object({
  name: z.string().trim().min(1).max(60),
  description: z.string().trim().max(500).nullable(),
  priceCents: z.number().int().min(0).max(1_000_000),
  stripePriceId: StripePriceIdSchema,
  allowances: AllowancesSchema,
  features: z.array(z.enum(FEATURE_KEYS)).max(FEATURE_KEYS.length),
  limits: LimitsSchema,
  active: z.boolean(),
  expectedVersion: z.number().int().min(1),
  reason: AdminReasonSchema,
});
export type AdminUpdatePlan = z.infer<typeof AdminUpdatePlanSchema>;

export const AdminUpdateBundleSchema = z.object({
  name: z.string().trim().min(1).max(60),
  quantity: z.number().int().positive().max(1_000_000),
  priceCents: z.number().int().min(0).max(1_000_000),
  availableOnPlans: z.array(z.enum(PLAN_KEYS)).max(PLAN_KEYS.length),
  stripePriceId: StripePriceIdSchema,
  active: z.boolean(),
  expectedVersion: z.number().int().min(1),
  reason: AdminReasonSchema,
});
export type AdminUpdateBundle = z.infer<typeof AdminUpdateBundleSchema>;

/** PUT /admin/vat: new platform VAT rate in basis points (2400 = 24%). */
export const AdminUpdateVatSchema = z.object({
  rateBps: z.number().int().min(0).max(MAX_VAT_RATE_BPS),
  reason: AdminReasonSchema,
});
export type AdminUpdateVat = z.infer<typeof AdminUpdateVatSchema>;

export const CatalogVersionSchema = z.object({
  version: z.number(),
  snapshot: z.record(z.string(), z.unknown()),
  changedBy: z.string().nullable(),
  changedByEmail: z.string().nullable(),
  reason: z.string().nullable(),
  createdAt: IsoDateTimeSchema,
});
export type CatalogVersion = z.infer<typeof CatalogVersionSchema>;
export const CatalogHistoryResponseSchema = z.object({ items: z.array(CatalogVersionSchema) });

// ─── model routing ──────────────────────────────────────────────────────────
export const AdminModelRouteSchema = ModelRouteSchema.extend({
  updatedBy: z.string().nullable(),
  updatedAt: IsoDateTimeSchema,
});
export type AdminModelRoute = z.infer<typeof AdminModelRouteSchema>;
export const AdminModelRoutesResponseSchema = z.object({
  items: z.array(AdminModelRouteSchema),
  providers: z.array(z.enum(AI_PROVIDERS)),
  /** Models with an explicit rate in the credit policy (others are metered at the default rate). */
  pricedModels: z.array(z.string()).default([]),
});

export const AdminUpdateModelRouteSchema = z.object({
  provider: z.enum(AI_PROVIDERS),
  model: z.string().trim().min(1).max(200),
  fallbacks: z
    .array(z.object({ provider: z.enum(AI_PROVIDERS), model: z.string().trim().min(1).max(200) }))
    .max(5)
    .default([]),
  temperature: z.number().min(0).max(2).nullable(),
  effort: z.enum(AI_EFFORTS).nullable().default(null),
  maxOutputTokens: z.number().int().positive().max(200_000),
  maxToolCalls: z.number().int().min(0).max(100),
  creditBudget: z.number().int().min(0).max(100_000),
  promptVersion: z.string().trim().min(1).max(40),
  enabled: z.boolean(),
  expectedVersion: z.number().int().min(1),
  reason: AdminReasonSchema,
});
export type AdminUpdateModelRoute = z.input<typeof AdminUpdateModelRouteSchema>;
export const AdminModelPurposeParamSchema = z.object({ purpose: AiPurposeSchema });

export const ModelRouteVersionSchema = z.object({
  version: z.number(),
  snapshot: z.record(z.string(), z.unknown()),
  changedBy: z.string().nullable(),
  changedByEmail: z.string().nullable(),
  reason: z.string().nullable(),
  createdAt: IsoDateTimeSchema,
});
export type ModelRouteVersion = z.infer<typeof ModelRouteVersionSchema>;
export const ModelRouteHistoryResponseSchema = z.object({
  items: z.array(ModelRouteVersionSchema),
});

// ─── AI pricing (platform_config `ai.creditPolicy`) ─────────────────────────
/**
 * Bounds for an admin-edited credit policy. Rates are micro-USD per token, which is the same
 * number as USD per 1M tokens; above $1 000 per 1M tokens is treated as a typo.
 */
export const AI_PRICING_LIMITS = {
  maxRate: 1000,
  minUnit: 100,
  maxUnit: 10_000_000,
  minThreshold: 1000,
  maxThreshold: 10_000_000,
  minCacheWriteMultiplier: 1,
  maxCacheWriteMultiplier: 10,
  maxMinimumCredits: 1000,
  maxModels: 100,
} as const;
const L = AI_PRICING_LIMITS;
const AdminRateSchema = z.number().finite().min(0).max(L.maxRate);
/** Input and output must cost something (a zero rate would make AI free); cache reads may be 0. */
const AdminPaidRateSchema = AdminRateSchema.positive();
const AdminRatesSchema = z.strictObject({
  input: AdminPaidRateSchema,
  output: AdminPaidRateSchema,
  cachedInput: AdminRateSchema,
});
const AdminModelRatesSchema = AdminRatesSchema.extend({
  longContext: AdminRatesSchema.extend({
    thresholdInputTokens: z.number().int().min(L.minThreshold).max(L.maxThreshold),
  }).optional(),
}).superRefine((r, ctx) => {
  // A long-prompt tier is a surcharge: each tier rate at least its base rate (the runtime's
  // output cap and reservation assume a long prompt never bills less).
  if (!r.longContext) return;
  for (const k of ['input', 'output', 'cachedInput'] as const)
    if (r.longContext[k] < r[k])
      ctx.addIssue({
        code: 'custom',
        path: ['longContext', k],
        message: 'long-prompt rate must not be below the base rate',
      });
});
/** No whitespace trimming: two ids differing only in spaces must not silently merge. */
export const AiModelIdSchema = z.string().regex(/^[A-Za-z0-9][A-Za-z0-9._:/@-]{0,199}$/);
const MinimumCreditsSchema = z.number().int().min(1).max(L.maxMinimumCredits);
export const AdminAiCreditPolicySchema = z
  .strictObject({
    /** Micro-USD of model cost per credit (20 000 = $0.02). */
    unit: z.number().int().min(L.minUnit).max(L.maxUnit),
    defaultRates: AdminModelRatesSchema,
    modelRates: z
      .record(AiModelIdSchema, AdminModelRatesSchema)
      .refine((r) => Object.keys(r).length <= L.maxModels, { message: 'too many models' }),
    cacheWriteMultiplier: z
      .number()
      .finite()
      .min(L.minCacheWriteMultiplier)
      .max(L.maxCacheWriteMultiplier),
    minimumByPurpose: z.partialRecord(AiPurposeSchema, MinimumCreditsSchema),
    defaultMinimum: MinimumCreditsSchema,
  })
  .superRefine((p, ctx) => {
    // Every AI feature passes a fixed credit cap per run, and a run whose cap is below its
    // purpose minimum is rejected: a minimum above the cap would break that feature outright.
    for (const purpose of AI_PURPOSES) {
      const min = p.minimumByPurpose[purpose] ?? p.defaultMinimum;
      const cap = AI_PURPOSE_CREDIT_CAPS[purpose];
      if (min > cap)
        ctx.addIssue({
          code: 'custom',
          path:
            p.minimumByPurpose[purpose] !== undefined
              ? ['minimumByPurpose', purpose]
              : ['defaultMinimum'],
          message: `minimum for ${purpose} must not exceed its per-run cap of ${cap} credits`,
          params: { purpose, cap },
        });
    }
  });
export type AdminAiCreditPolicy = z.infer<typeof AdminAiCreditPolicySchema>;

export const AdminAiRoutedModelSchema = z.object({
  purpose: AiPurposeSchema,
  provider: z.enum(AI_PROVIDERS),
  model: z.string(),
  role: z.enum(['primary', 'fallback']),
  enabled: z.boolean(),
  /** Has its own rate (otherwise it is metered at `defaultRates`). */
  priced: z.boolean(),
});
export type AdminAiRoutedModel = z.infer<typeof AdminAiRoutedModelSchema>;

export const AdminAiBundleSchema = z.object({
  key: z.string(),
  name: z.string(),
  quantity: z.number(),
  priceCents: z.number(),
  currency: z.string(),
  active: z.boolean(),
  availableOnPlans: z.array(z.enum(PLAN_KEYS)),
});
export type AdminAiBundle = z.infer<typeof AdminAiBundleSchema>;

export const AdminAiPricingSchema = z.object({
  /** Effective policy: code defaults merged with the stored override (what new runs use). */
  policy: AiCreditPolicySchema,
  /** Code defaults (what "reset to defaults" restores). */
  defaults: AiCreditPolicySchema,
  /** A stored override is in effect (false = code defaults). */
  overridden: z.boolean(),
  /** Optimistic-concurrency version of `ai.creditPolicy` (0 = never edited). */
  version: z.number().int(),
  updatedAt: IsoDateTimeSchema.nullable(),
  updatedBy: z.string().nullable(),
  updatedByEmail: z.string().nullable(),
  /** Every model referenced by `model_routes` (primary + fallbacks). */
  routedModels: z.array(AdminAiRoutedModelSchema),
  /** Real (non-fake) routed models without their own rate. */
  unpricedRoutedModels: z.array(z.string()),
  /** AI top-up bundles (current prices) for the margin indicator. */
  aiBundles: z.array(AdminAiBundleSchema),
});
export type AdminAiPricing = z.infer<typeof AdminAiPricingSchema>;

export const AdminUpdateAiPricingSchema = z.object({
  policy: AdminAiCreditPolicySchema,
  expectedVersion: z.number().int().min(0),
  reason: AdminReasonSchema,
});
export type AdminUpdateAiPricing = z.infer<typeof AdminUpdateAiPricingSchema>;

export const AdminResetAiPricingSchema = z.object({
  expectedVersion: z.number().int().min(0),
  reason: AdminReasonSchema,
});

export const AdminAiPricingHistoryEntrySchema = z.object({
  id: z.string(),
  action: z.string(),
  fromVersion: z.number().nullable(),
  toVersion: z.number().nullable(),
  reason: z.string().nullable(),
  actorId: z.string(),
  actorEmail: z.string().nullable(),
  changed: z.array(z.string()),
  before: z.record(z.string(), z.unknown()).nullable(),
  after: z.record(z.string(), z.unknown()).nullable(),
  createdAt: IsoDateTimeSchema,
});
export type AdminAiPricingHistoryEntry = z.infer<typeof AdminAiPricingHistoryEntrySchema>;
export const AdminAiPricingHistorySchema = z.object({
  items: z.array(AdminAiPricingHistoryEntrySchema),
});

// ─── provider health ────────────────────────────────────────────────────────
export const ProviderHealthSchema = z.object({
  generatedAt: IsoDateTimeSchema,
  webhooks: z.object({
    stripe: z.object({
      byStatus: z.record(z.string(), z.number()),
      stuck: z.number(),
      failed24h: z.number(),
      recentFailures: z.array(
        z.object({
          id: z.string(),
          type: z.string(),
          error: z.string().nullable(),
          attempts: z.number(),
          receivedAt: IsoDateTimeSchema,
        }),
      ),
    }),
    providers: z.array(
      z.object({
        provider: z.string(),
        received: z.number(),
        processed: z.number(),
        ignored: z.number(),
        failed: z.number(),
        pending: z.number(),
      }),
    ),
    recentProviderFailures: z.array(
      z.object({
        id: z.string(),
        provider: z.string(),
        eventType: z.string().nullable(),
        error: z.string().nullable(),
        receivedAt: IsoDateTimeSchema,
      }),
    ),
  }),
  deliveries: z.array(
    z.object({
      provider: z.string(),
      channel: z.string(),
      total24h: z.number(),
      failed24h: z.number(),
      total7d: z.number(),
      failed7d: z.number(),
      bounced7d: z.number(),
      errorRate7d: z.number(),
      topErrors: z.array(z.object({ code: z.string(), count: z.number() })),
    }),
  ),
  jobs: z.object({
    available: z.boolean(),
    queues: z.array(
      z.object({
        name: z.string(),
        queued: z.number(),
        retry: z.number(),
        active: z.number(),
        stuck: z.number(),
        failed24h: z.number(),
      }),
    ),
  }),
  workflows: z.object({ stalled: z.number(), failed24h: z.number() }),
  agentRuns: z.object({ stuck: z.number(), failed24h: z.number() }),
  reconciliation: z.object({
    openAnomalies: z.number(),
    lastRunAt: IsoDateTimeSchema.nullable(),
    lastRunStatus: z.string().nullable(),
  }),
});
export type ProviderHealth = z.infer<typeof ProviderHealthSchema>;

// ─── platform audit log ─────────────────────────────────────────────────────
export const AdminAuditQuerySchema = z.object({
  action: z.string().trim().max(60).optional(),
  tenantId: UuidSchema.optional(),
  actorId: z.string().trim().max(128).optional(),
  cursor: z.string().max(120).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});
export const AdminAuditEntrySchema = z.object({
  id: UuidSchema,
  actorId: z.string(),
  actorEmail: z.string().nullable(),
  action: z.string(),
  tenantId: UuidSchema.nullable(),
  tenantName: z.string().nullable(),
  targetType: z.string().nullable(),
  targetId: z.string().nullable(),
  reason: z.string().nullable(),
  meta: z.record(z.string(), z.unknown()),
  requestId: z.string().nullable(),
  createdAt: IsoDateTimeSchema,
});
export type AdminAuditEntry = z.infer<typeof AdminAuditEntrySchema>;
export const AdminAuditResponseSchema = z.object({
  items: z.array(AdminAuditEntrySchema),
  nextCursor: z.string().nullable(),
});
